import { useMutation } from '@apollo/client';
import { ECloneWorldMarkerStoryCloneType } from '__generated__/globalTypes';
import {
    CloneWorldMapMarker,
    CloneWorldMapMarkerVariables
} from 'graphql/mutation/__generated__/CloneWorldMapMarker';
import { CLONE_WORLD_MAP_MARKER } from 'graphql/mutation/worldMutation';
import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import React, { useContext } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';

interface CloneOpts {
    cloneInteractivity: ECloneWorldMarkerStoryCloneType;
}
type CloneFn = (markerId: string, opts: CloneOpts) => void;

export const useCloneWorldMarker = (): CloneFn => {
    const ctxEdit = useContext(CtxEdit);
    const worldMarker = useWorldMarker();
    const { getTokenForMapId } = useWorldMapLocalStorage();

    const [cloneMarker] = useMutation<CloneWorldMapMarker, CloneWorldMapMarkerVariables>(
        CLONE_WORLD_MAP_MARKER
    );

    async function clone(markerId: string, opts: CloneOpts) {
        const marker = ctxEdit.worldMarkers.find((e) => e.id === markerId);
        if (!marker) {
            return;
        }

        const newId = Math.random().toString();

        const newMarker: IWorldMarker = {
            ...marker,
            id: newId,
            order: worldMarker.findHighestOrder() + 1
        };
        if ('ref' in newMarker) {
            newMarker.ref = React.createRef();
        }

        const response = await cloneMarker({
            variables: {
                worldMapMarkerId: marker.id,
                newWorldMapMarkerId: newId,
                cloneStoryType: opts.cloneInteractivity,
                token: getTokenForMapId(ctxEdit.map.worldMapId)
            }
        });

        if (opts.cloneInteractivity === ECloneWorldMarkerStoryCloneType.DUPLICATE_STORY) {
            newMarker.storyIds =
                response.data?.cloneWorldMarker?.stories?.map((e) => e.storyId) ?? [];
        }
        if (opts.cloneInteractivity === ECloneWorldMarkerStoryCloneType.EMPTY) {
            newMarker.storyIds = [];
        }

        if (marker.hasBookingConfiguration) {
            newMarker.isBookingAvailable = true;
        }

        ctxEdit.addWorldMarker(newMarker, true);
        ctxEdit.setSelectedMarkers([newMarker.id]);
    }

    return clone;
};
